/**
 * Subscriptions Management Page
 */

import React, { useState } from 'react';
import { Card, Button, Badge } from 'flowbite-react';
import { HiCheck } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { stripeApi } from '../api/stripe';

interface TierConfig {
  id: string;
  name: string;
  displayName: string;
  priceContributing: number;
  pricePrivate: number;
  perUser: boolean;
  features: string[];
  highlightedFeatures: string[];
  standardsBadge: string;
  highlighted?: boolean;
  isEnterprise?: boolean;
}

const TIERS: TierConfig[] = [
  {
    id: 'team',
    name: 'Team',
    displayName: 'Pro Solo',
    priceContributing: 49,
    pricePrivate: 99,
    perUser: true,
    standardsBadge: '808 Community Rules',
    highlightedFeatures: [
      '67 open + community standards',
      'Context-aware injection',
      'Relevance scoring',
    ],
    features: [
      '5 participants, 5 projects',
      'Shared team standards',
      'Cross-platform sync',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    displayName: 'Pro',
    priceContributing: 149,
    pricePrivate: 199,
    perUser: true,
    standardsBadge: '808 Community + Compliance + AWS',
    highlighted: true,
    highlightedFeatures: [
      'Full community library (67 standards)',
      'Standards picker (per-user control)',
      'Compliance + AWS standards prioritized',
      '10 participants, unlimited projects',
      'Analytics dashboard',
    ],
    features: [
      'All Pro Solo features',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    displayName: 'Enterprise',
    priceContributing: 0,
    pricePrivate: 0,
    perUser: false,
    isEnterprise: true,
    standardsBadge: '2,088 Rules + Custom Packs',
    highlightedFeatures: [
      '174 enterprise-curated standards (2,088 rules)',
      'Plus full community library (67 standards)',
      'SSO integration',
      'Executive reports',
      'Knowledge capture',
    ],
    features: [
      '25+ seats',
      'All Pro features',
      'Dedicated support',
    ],
  },
];

const Subscriptions: React.FC = () => {
  const { client, refreshUserData, isLoading: isStatusLoading } = useStatus();
  const [contributionMode, setContributionMode] = useState<'contributing' | 'private'>('private');
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentTier = client?.subscription_tier || '';
  const currentPlan = TIERS.find(tier => tier.id === currentTier);
  const hasKnownPlan = currentTier === 'free' || Boolean(currentPlan);
  const currentPlanName = isStatusLoading ? 'Loading…' : currentTier === 'free' ? 'Free' : currentPlan?.displayName || 'Unknown';

  const handleSelectTier = async (tierId: string) => {
    if (isStatusLoading || !hasKnownPlan || tierId === currentTier) return;

    setIsLoading(tierId);
    setError(null);

    try {
      if (currentTier === 'free' || !client?.stripe_customer_id) {
        // No existing paid subscription — create Stripe checkout session
        const response = await stripeApi.createCheckoutSession(tierId, contributionMode);
        const checkoutUrl = (response.data as any)?.data?.Records?.[0]?.checkoutUrl;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
        setError(response.error || 'Failed to create checkout session');
      } else {
        // Existing subscription — upgrade/downgrade in place
        const response = await stripeApi.updateSubscription(tierId);
        if (response.success) {
          await refreshUserData();
        } else {
          setError(response.error || 'Failed to update subscription');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process subscription');
    } finally {
      setIsLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setIsLoading('billing');
    try {
      const response = await stripeApi.getBillingPortal();
      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      } else {
        setError(response.error || 'Failed to open billing portal');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    } finally {
      setIsLoading(null);
    }
  };

  const getTierIndex = (tierId: string) => TIERS.findIndex((t) => t.id === tierId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Subscription</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your subscription and billing
        </p>
      </div>

      {/* Current Plan */}
      <Card aria-label="Current subscription">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Current Plan</h3>
            <p className="text-2xl font-bold text-mindmeld-primary">
              {currentPlanName}
            </p>
            <p className="text-sm text-gray-500">
              {isStatusLoading ? 'Retrieving subscription details' : !hasKnownPlan ? 'Subscription details unavailable' : client?.subscription_status === 'active' ? 'Active' : client?.subscription_status || 'Status unavailable'}
              {!isStatusLoading && currentPlan && client?.current_period_end && (
                <> - Renews {new Date(client.current_period_end).toLocaleDateString()}</>
              )}
            </p>
          </div>
          <Button color="gray" onClick={handleManageBilling} disabled={isStatusLoading || isLoading === 'billing'}>
            {isLoading === 'billing' ? 'Loading...' : 'Manage Billing'}
          </Button>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md">{error}</div>
      )}

      {/* Standards Library Highlight */}
      <Card className="bg-indigo-50 border-indigo-200">
        <div className="text-center">
          <span className="text-3xl font-bold text-indigo-700">2,088</span>
          <span className="text-lg text-indigo-600 ml-2">curated rules</span>
          <span className="text-gray-500 mx-2">across</span>
          <span className="text-3xl font-bold text-indigo-700">174</span>
          <span className="text-lg text-indigo-600 ml-2">standards</span>
          <span className="text-gray-500 mx-2">in</span>
          <span className="text-3xl font-bold text-indigo-700">11</span>
          <span className="text-lg text-indigo-600 ml-2">categories</span>
          <p className="text-sm text-gray-600 mt-2">
            67 open + community standards available on every plan.
            Enterprise unlocks 107 additional curated standards with a higher quality bar.
          </p>
        </div>
      </Card>

      {/* Contributing / Private Toggle */}
      <div className="text-center">
        <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-white">
          <button
            className={`px-6 py-2 text-sm font-medium rounded-md ${
              contributionMode === 'contributing'
                ? 'bg-mindmeld-primary text-white'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setContributionMode('contributing')}
          >
            Contributing
          </button>
          <button
            className={`px-6 py-2 text-sm font-medium rounded-md ${
              contributionMode === 'private'
                ? 'bg-mindmeld-primary text-white'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setContributionMode('private')}
          >
            Private
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 max-w-lg mx-auto">
          {contributionMode === 'contributing'
            ? 'Anonymized standard categories (not code, not prompts) contribute to community packs. You approve each contribution.'
            : 'Your learnings stay with your organization only.'}
        </p>
      </div>

      {/* Pricing Tiers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIERS.map((tier) => {
          const isCurrentTier = !isStatusLoading && tier.id === currentTier;
          const tierIndex = getTierIndex(tier.id);
          const currentTierIndex = getTierIndex(currentTier);
          const isUpgrade = tierIndex > currentTierIndex;

          const price = contributionMode === 'contributing' ? tier.priceContributing : tier.pricePrivate;

          return (
            <Card
              key={tier.id}
              className={`${tier.highlighted ? 'border-2 border-mindmeld-primary' : ''} ${
                isCurrentTier ? 'bg-blue-50' : ''
              }`}
            >
              {tier.highlighted && (
                <Badge color="info" className="mb-2">
                  Most Popular
                </Badge>
              )}
              <h3 className="text-lg font-semibold text-gray-900">{tier.displayName}</h3>

              {/* Pricing */}
              <div className="mt-2">
                {tier.isEnterprise ? (
                  <div>
                    <span className="text-2xl font-bold text-gray-900">Contact Sales</span>
                    <p className="text-xs text-gray-500 mt-1">25+ seats</p>
                  </div>
                ) : (
                  <div>
                    <span className="text-3xl font-bold text-gray-900">${price}</span>
                    <span className="text-gray-500">/mo per user</span>
                  </div>
                )}
              </div>

              {/* Standards Badge */}
              <div className="mt-3">
                <Badge color="info" size="sm">
                  {tier.standardsBadge}
                </Badge>
              </div>

              {/* Features */}
              <ul className="mt-4 space-y-2">
                {tier.highlightedFeatures.map((feature) => (
                  <li key={feature} className="flex items-start text-sm">
                    <HiCheck className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-gray-900 font-medium">{feature}</span>
                  </li>
                ))}
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start text-sm">
                    <HiCheck className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6">
                {tier.isEnterprise ? (
                  <Button
                    className="w-full"
                    color="light"
                    onClick={() => window.location.href = 'mailto:sales@mindmeld.dev'}
                  >
                    Contact Sales
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    color={isCurrentTier ? 'gray' : tier.highlighted ? 'info' : 'light'}
                    disabled={isStatusLoading || !hasKnownPlan || isCurrentTier || isLoading === tier.id}
                    onClick={() => handleSelectTier(tier.id)}
                  >
                    {isLoading === tier.id
                      ? 'Processing...'
                      : isCurrentTier
                      ? 'Current Plan'
                      : isStatusLoading || !hasKnownPlan
                      ? 'Plan details unavailable'
                      : isUpgrade
                      ? 'Upgrade'
                      : 'Downgrade'}
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Contribution Savings Note */}
      <div className="text-center text-sm text-gray-500 bg-white rounded-lg border border-gray-200 p-4">
        Contributing mode saves $50-100/month and helps build the community standards library.
      </div>

      {/* FAQ */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Frequently Asked Questions</h3>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900">Can I change plans at any time?</h4>
            <p className="text-sm text-gray-500">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately,
              and we'll prorate your billing.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">What happens to my data if I change plans?</h4>
            <p className="text-sm text-gray-500">
              Your data is always preserved. If you move to a plan with lower limits
              (e.g., fewer projects or participants), you won&apos;t be able to create new ones until you&apos;re under the limit.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Do you offer refunds?</h4>
            <p className="text-sm text-gray-500">
              We offer a 14-day money-back guarantee for new subscriptions. Contact support for assistance.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Subscriptions;
